import { useCustom, useCustomMutation, useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { graphqlErrorInfo } from '../../../utils/graphqlErrors';

/**
 * E50 · Backoffice — Erasure log (board cc-backoffice ch02 K1, read model ErasureRequests). The
 * default view is the operator's queue — every open in-app request, oldest first, with its due date
 * and whether the privacy inbox was told (`cli.js requests`); "Completed" and "All" show the history:
 * who erased, when, why, each store's outcome, the practices to tell. Status is derived by the server.
 */
type Step = {
  store: string;
  outcome: string;
  counts: string;
  note: string | null;
  until: string | null;
};
type Row = {
  subject: string;
  status: 'requested' | 'completed';
  requestedAt: string | null;
  dueBy: string | null;
  notified: string | null;
  reason: string | null;
  performer: string | null;
  startedAt: string | null;
  completedAt: string | null;
  steps: Step[];
  keptEvents: number | null;
  keptRows: string | null;
  affectedPractices: string[];
};
type Status = 'requested' | 'completed' | 'all';

const ROW_FIELDS = [
  'subject',
  'status',
  'requestedAt',
  'dueBy',
  'notified',
  'reason',
  'performer',
  'startedAt',
  'completedAt',
  { steps: ['store', 'outcome', 'counts', 'note', 'until'] },
  'keptEvents',
  'keptRows',
  'affectedPractices',
];

export const BackofficeErasureLog = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const [status, setStatus] = useState<Status>('requested');
  const [failure, setFailure] = useState<string | null>(null);
  const { data, isFetching, isError, refetch } = useCustom<Row[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'erasureRequests',
      fields: ROW_FIELDS,
      variables: { status: { name: 'status', type: 'String', value: status } },
    },
  });
  const rows = data?.data ?? [];
  const { mutateAsync, isLoading: finishing } = useCustomMutation();

  const day = (iso: string | null) =>
    iso ? format(new Date(iso), 'yyyy-MM-dd') : '—';
  const due = (r: Row) => {
    if (r.status !== 'requested' || !r.dueBy) return null;
    const left = Math.ceil((Date.parse(r.dueBy) - Date.now()) / 86_400_000);
    return left >= 0 ? (
      <Tag color={left <= 7 ? 'orange' : 'default'}>
        {translate('backoffice.erasureLog.daysLeft', { count: left })}
      </Tag>
    ) : (
      <Tag color="red">
        {translate('backoffice.erasureLog.overdue', { count: -left })}
      </Tag>
    );
  };

  const finish = async (subject: string) => {
    setFailure(null);
    try {
      await mutateAsync({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'finishErasure',
          fields: ['store', 'outcome'],
          variables: {
            subject: { name: 'subject', type: 'String!', value: subject },
          },
        },
      });
      message.success(translate('backoffice.erasureLog.finished'));
      await refetch();
    } catch (error) {
      setFailure(
        graphqlErrorInfo(error).message ||
          translate('backoffice.erasureLog.failed'),
      );
    }
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3}>
          {translate('backoffice.erasureLog.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">
          {translate('backoffice.erasureLog.intro')}
        </Typography.Paragraph>
      </div>
      <Segmented
        value={status}
        onChange={(v) => setStatus(v as Status)}
        options={(['requested', 'completed', 'all'] as const).map((s) => ({
          value: s,
          label: translate(`backoffice.erasureLog.status.${s}`),
        }))}
        data-testid="erasure-log-status"
      />
      {isError ? (
        <Alert
          type="error"
          showIcon
          message={translate('backoffice.erasureLog.failed')}
        />
      ) : null}
      {failure ? <Alert type="error" showIcon message={failure} /> : null}
      <Table<Row>
        scroll={{ x: 'max-content' }}
        rowKey={(r) => `${r.subject}:${r.requestedAt ?? r.startedAt}`}
        loading={isFetching}
        dataSource={rows}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        locale={{ emptyText: translate('backoffice.erasureLog.empty') }}
        data-testid="erasure-log-table"
        onRow={(r) =>
          ({ 'data-testid': `erasure-log-row-${r.subject}` } as never)
        }
        expandable={{
          rowExpandable: (r) => r.steps.length > 0,
          expandedRowRender: (r) => (
            <Table<Step>
              scroll={{ x: 'max-content' }}
              size="small"
              rowKey="store"
              pagination={false}
              dataSource={r.steps}
              columns={[
                {
                  title: translate('backoffice.erasure.store'),
                  dataIndex: 'store',
                },
                {
                  title: translate('backoffice.erasure.outcome'),
                  dataIndex: 'outcome',
                  render: (v: string) => <Tag>{v}</Tag>,
                },
                {
                  title: translate('backoffice.erasure.counts'),
                  dataIndex: 'counts',
                  render: (v: string) => <code>{v}</code>,
                },
                {
                  title: translate('backoffice.erasure.note'),
                  dataIndex: 'note',
                },
              ]}
            />
          ),
        }}
        columns={[
          {
            title: translate('backoffice.erasureLog.subject'),
            dataIndex: 'subject',
            render: (v: string) => <code>{v}</code>,
          },
          {
            title: translate('backoffice.erasure.outcome'),
            dataIndex: 'status',
            render: (v: Row['status']) => (
              <Tag
                color={v === 'completed' ? 'green' : 'blue'}
                data-testid="erasure-log-status-tag"
              >
                {translate(`backoffice.erasureLog.status.${v}`)}
              </Tag>
            ),
          },
          {
            title: translate('backoffice.erasureLog.requestedAt'),
            render: (_, r) => day(r.requestedAt),
          },
          {
            title: translate('backoffice.erasureLog.dueBy'),
            render: (_, r) => (
              <Space size={4}>
                {day(r.dueBy)}
                {due(r)}
              </Space>
            ),
          },
          {
            title: translate('backoffice.erasureLog.notified'),
            render: (_, r) =>
              r.requestedAt
                ? r.notified ?? (
                    <Tag color="orange">
                      {translate('backoffice.erasureLog.notYet')}
                    </Tag>
                  )
                : '—',
          },
          {
            title: translate('backoffice.erasureLog.completedAt'),
            render: (_, r) => day(r.completedAt),
          },
          {
            title: translate('backoffice.erasureLog.performer'),
            dataIndex: 'performer',
          },
          {
            title: translate('backoffice.erasureLog.reason'),
            dataIndex: 'reason',
          },
          {
            title: translate('backoffice.erasureLog.practices'),
            render: (_, r) => r.affectedPractices.join(', '),
          },
          {
            title: translate('table.actions'),
            render: (_, r) =>
              r.status === 'requested' ? (
                <Button
                  size="small"
                  onClick={() =>
                    navigate(
                      `/backoffice/erasure?subject=${encodeURIComponent(
                        r.subject,
                      )}`,
                    )
                  }
                  data-testid={`erasure-log-inventory-${r.subject}`}
                >
                  {translate('backoffice.erasureLog.inventory')}
                </Button>
              ) : r.steps.some((s) => s.outcome === 'deferred') ? (
                <Button
                  size="small"
                  loading={finishing}
                  onClick={() => finish(r.subject)}
                >
                  {translate('backoffice.erasureLog.finish')}
                </Button>
              ) : null,
          },
        ]}
      />
    </Space>
  );
};
